import { useState, useEffect, useCallback } from 'react';
import qualificationService from '../services/qualificationService';
import i18n from '../i18n/config';
import { Qualification, QualificationCreate, QualificationBase } from '../types'; // Remplacer QualificationUpdate par QualificationBase

interface UseUseCaseQualificationResult {
  latestQualification: Qualification | null;
  qualificationHistory: Qualification[];
  isLoading: boolean;
  error: string | null;
  fetchLatestQualification: () => Promise<void>;
  fetchQualificationHistory: () => Promise<void>;
  createQualification: (data: QualificationCreate) => Promise<Qualification | null>;
  updateQualification: (qualificationId: string, data: QualificationBase) => Promise<Qualification | null>; // Utiliser QualificationBase pour la mise à jour
  deleteQualification: (qualificationId: string) => Promise<boolean>;
  // Ajouter un état pour la qualification en cours d'édition
  editingQualification: Qualification | null;
  setEditingQualification: React.Dispatch<React.SetStateAction<Qualification | null>>;
}

interface UseUseCaseQualificationOptions {
  onQualificationCreated?: () => Promise<void>;
}

export const useUseCaseQualification = (
  useCaseId: string | undefined,
  options?: UseUseCaseQualificationOptions
): UseUseCaseQualificationResult => {
  const [latestQualification, setLatestQualification] = useState<Qualification | null>(null);
  const [qualificationHistory, setQualificationHistory] = useState<Qualification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingQualification, setEditingQualification] = useState<Qualification | null>(null); // Nouvel état

  const handleApiError = (err: any, context: string, fallbackKey: string): string => {
    console.error(`Error ${context}:`, err);
    const errorMsg = err.response?.data?.detail || err.message || i18n.t(fallbackKey);
    setError(errorMsg);
    return errorMsg;
  };

  const fetchLatestQualification = useCallback(async () => {
    if (!useCaseId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await qualificationService.getLatest(useCaseId);
      setLatestQualification(data); // data can be null if no qualification exists
    } catch (err: any) {
      handleApiError(err, 'fetching latest qualification', 'errors.qualification.fetchLatestFailed');
      setLatestQualification(null);
    } finally {
      setIsLoading(false);
    }
  }, [useCaseId]);

  const fetchQualificationHistory = useCallback(async () => {
    if (!useCaseId) return;
    // Peut partager isLoading ou avoir son propre état si nécessaire
    setIsLoading(true);
    setError(null);
    try {
      const data = await qualificationService.getHistory(useCaseId);
      setQualificationHistory(data || []);
    } catch (err) {
      handleApiError(err, 'fetching qualification history', 'errors.qualification.fetchHistoryFailed');
      setQualificationHistory([]);
    } finally {
      // Gérer isLoading en fonction de si c'est partagé ou non
      setIsLoading(false);
    }
  }, [useCaseId]);

  const createQualification = useCallback(async (data: QualificationCreate): Promise<Qualification | null> => {
    if (!useCaseId) {
      setError(i18n.t('errors.useCase.missingId'));
      return null;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await qualificationService.create(useCaseId, data);
      // Recharger les données après création
      await fetchLatestQualification();
      await fetchQualificationHistory();
      // Call the callback to refresh parent use case data (status may have changed)
      if (options?.onQualificationCreated) {
        await options.onQualificationCreated();
      }
      return result;
    } catch (err) {
      handleApiError(err, 'creating qualification', 'errors.qualification.createFailed');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [useCaseId, fetchLatestQualification, fetchQualificationHistory, options]);

  const updateQualification = useCallback(async (qualificationId: string, data: QualificationBase): Promise<Qualification | null> => { // Utiliser QualificationBase
     if (!useCaseId) {
       setError(i18n.t('errors.useCase.missingId'));
       return null;
     }
    setIsLoading(true);
    setError(null);
    try {
      // L'endpoint PUT doit inclure l'ID du cas d'usage pour le contexte
      const result = await qualificationService.update(useCaseId, qualificationId, data);
       // Recharger les données après mise à jour
      await fetchLatestQualification();
      await fetchQualificationHistory();
      return result;
    } catch (err) {
      handleApiError(err, 'updating qualification', 'errors.qualification.updateFailed');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [useCaseId, fetchLatestQualification, fetchQualificationHistory]); // useCaseId est une dépendance indirecte via les fetchs

  const deleteQualification = useCallback(async (qualificationId: string): Promise<boolean> => {
     if (!useCaseId) {
       setError(i18n.t('errors.useCase.missingId'));
       return false;
     }
    setIsLoading(true);
    setError(null);
    try {
       // L'endpoint DELETE doit inclure l'ID du cas d'usage pour le contexte
      await qualificationService.delete(useCaseId, qualificationId);
       // Recharger les données après suppression
      await fetchLatestQualification();
      await fetchQualificationHistory();
      return true;
    } catch (err) {
      handleApiError(err, 'deleting qualification', 'errors.qualification.deleteFailed');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [useCaseId, fetchLatestQualification, fetchQualificationHistory]); // useCaseId est une dépendance indirecte via les fetchs


  // Charger les données initiales lors du montage ou quand useCaseId change
  useEffect(() => {
    if (useCaseId) {
      fetchLatestQualification();
      fetchQualificationHistory();
    } else {
      // Réinitialiser si pas d'ID
      setLatestQualification(null);
      setQualificationHistory([]);
      setError(null);
    }
  }, [useCaseId, fetchLatestQualification, fetchQualificationHistory]); // Inclure les fonctions useCallback comme dépendances

  return {
    latestQualification,
    qualificationHistory,
    isLoading,
    error,
    fetchLatestQualification,
    fetchQualificationHistory,
    createQualification,
    updateQualification,
    deleteQualification,
    editingQualification, // Retourner le nouvel état
    setEditingQualification, // Retourner la fonction pour le définir (utilisé par onEdit dans History)
  };
};
